/* Cordings — mobile menu behaviour (tabs + L2/L3 drill-down).
   Attaches to .cds-mob-menu. Decoupled from CordingsHeader:
   uses a MutationObserver to reset state when the drawer closes. */

class CordingsMobMenu {
  constructor(el) {
    this.el = el;
    this._l2Key = null;
    this._l3Key = null;
    this._init();
  }

  _init() {
    // Tab buttons
    this.el.querySelectorAll('[data-mob-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this._switchTab(tab.dataset.mobTab));
    });

    // L1 items that open a sub-panel
    this.el.querySelectorAll('[data-mob-open-l2]').forEach((btn) => {
      btn.addEventListener('click', () => this._openL2(btn.dataset.mobOpenL2));
    });

    // L2 items that open a sub-panel — delegated, since the L2 list is
    // replaced via innerHTML on every _openL2() call.
    const l2Links = this.el.querySelector('[data-mob-l2-links]');
    if (l2Links) {
      l2Links.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-mob-open-l3]');
        if (btn) this._openL3(btn.dataset.mobOpenL3);
      });
    }

    // Back button inside L2 view
    const back = this.el.querySelector('[data-mob-back]');
    if (back) back.addEventListener('click', () => this._closeL2());

    // Back button inside L3 view
    const backL3 = this.el.querySelector('[data-mob-back-l3]');
    if (backL3) backL3.addEventListener('click', () => this._closeL3());

    // Reset to L1 whenever the drawer is closed by CordingsHeader.
    // attributeOldValue lets us check the transition: is-open → gone, avoiding
    // an infinite loop where _reset() itself triggers classList changes.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const hadOpen = m.oldValue ? m.oldValue.includes('is-open') : false;
        if (hadOpen && !this.el.classList.contains('is-open')) {
          this._reset();
        }
      }
    });
    observer.observe(this.el, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
  }

  _switchTab(key) {
    this.el.querySelectorAll('[data-mob-tab]').forEach((tab) => {
      const active = tab.dataset.mobTab === key;
      tab.classList.toggle('cds-mob-tab--active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.el.querySelectorAll('[data-mob-l1]').forEach((panel) => {
      panel.hidden = panel.dataset.mobL1 !== key;
    });

    // Scroll L1 view back to top on tab switch
    const l1View = this.el.querySelector('[data-mob-view="l1"]');
    if (l1View) l1View.scrollTop = 0;
  }

  _openL2(key) {
    // Locate pre-rendered list in the hidden data store
    const store = this.el.querySelector(`[data-l2-key="${key}"]`);
    if (!store) return;

    // Update breadcrumb title
    const titleEl = this.el.querySelector('[data-mob-l2-title]');
    if (titleEl) titleEl.textContent = store.dataset.l2Title || '';

    // Populate the L2 links container with a fresh clone
    const linksContainer = this.el.querySelector('[data-mob-l2-links]');
    if (linksContainer) {
      linksContainer.innerHTML = '';
      linksContainer.appendChild(store.cloneNode(true));
    }

    // Reset L2 scroll before sliding in
    const l2View = this.el.querySelector('[data-mob-view="l2"]');
    if (l2View) l2View.scrollTop = 0;

    this._l2Key = key;
    this.el.classList.add('cds-mob-menu--l2');

    // Update aria-expanded on the L1 trigger
    const trigger = this.el.querySelector(`[data-mob-open-l2="${key}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  _closeL2() {
    this._closeL3();
    if (this._l2Key) {
      const trigger = this.el.querySelector(`[data-mob-open-l2="${this._l2Key}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    this._l2Key = null;
    this.el.classList.remove('cds-mob-menu--l2');
  }

  _openL3(key) {
    // Locate pre-rendered list in the hidden data store
    const store = this.el.querySelector(`[data-l3-key="${key}"]`);
    if (!store) return;

    // Update breadcrumb title
    const titleEl = this.el.querySelector('[data-mob-l3-title]');
    if (titleEl) titleEl.textContent = store.dataset.l3Title || '';

    // Populate the L3 links container with a fresh clone
    const linksContainer = this.el.querySelector('[data-mob-l3-links]');
    if (linksContainer) {
      linksContainer.innerHTML = '';
      linksContainer.appendChild(store.cloneNode(true));
    }

    // Reset L3 scroll before sliding in
    const l3View = this.el.querySelector('[data-mob-view="l3"]');
    if (l3View) l3View.scrollTop = 0;

    this._l3Key = key;
    this.el.classList.add('cds-mob-menu--l3');

    // Update aria-expanded on the L2 trigger
    const trigger = this.el.querySelector(`[data-mob-open-l3="${key}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  _closeL3() {
    if (this._l3Key) {
      const trigger = this.el.querySelector(`[data-mob-open-l3="${this._l3Key}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    this._l3Key = null;
    this.el.classList.remove('cds-mob-menu--l3');
  }

  _reset() {
    this._closeL2();
    // Reset tabs to the first (men) tab if tabs are present
    const firstTab = this.el.querySelector('[data-mob-tab]');
    if (firstTab && !firstTab.classList.contains('cds-mob-tab--active')) {
      this._switchTab(firstTab.dataset.mobTab);
    }
  }
}

// Initialise after the DOM is parsed (script is defer-loaded)
document.querySelectorAll('.cds-mob-menu').forEach((el) => new CordingsMobMenu(el));
